import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, MessageCircle } from 'lucide-react';
import api from '../lib/api';
import { formatIDR, formatDate, formatSizes, PAY_STATUS, TX_STATUS } from '../lib/format';

export default function CustomerDetail() {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => setCustomer(r.data));
  }, [id]);

  if (!customer) return <p className="text-sm text-mocha py-16 text-center">Memuat profil customer…</p>;

  let wa = (customer.whatsapp || '').replace(/[^0-9]/g, '');
  if (wa.startsWith('0')) wa = '62' + wa.slice(1);

  return (
    <div className="space-y-5 max-w-3xl" data-testid="customer-detail-page">
      <div className="flex items-center gap-3">
        <Link to="/customer" data-testid="back-to-customers" className="p-2 rounded-xl bg-white border border-border text-brown">
          <ArrowLeft size={18} />
        </Link>
        <h1 className="font-display text-2xl md:text-3xl text-ink tracking-tight">{customer.name}</h1>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1.5 text-sm">
            <p><span className="text-mocha">WhatsApp:</span> <b>{customer.whatsapp || '-'}</b></p>
            <p><span className="text-mocha">Alamat:</span> {customer.address || '-'}</p>
            {customer.notes && <p><span className="text-mocha">Catatan:</span> {customer.notes}</p>}
          </div>
          {wa && (
            <a
              data-testid="customer-detail-wa"
              href={`https://wa.me/${wa}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] text-white px-4 py-2 text-xs font-bold"
            >
              <MessageCircle size={14} /> Chat WhatsApp
            </a>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <div className="rounded-xl bg-ivory p-4 text-center">
            <p className="font-display text-2xl text-brown-dark" data-testid="customer-tx-count">{customer.transaction_count}</p>
            <p className="text-[10px] uppercase tracking-wider text-mocha">Order</p>
          </div>
          <div className="rounded-xl bg-ivory p-4 text-center">
            <p className="font-display text-2xl text-brown-dark" data-testid="customer-total-spent">{formatIDR(customer.total_spent)}</p>
            <p className="text-[10px] uppercase tracking-wider text-mocha">Total Belanja</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-5">
        <h2 className="font-display text-xl text-ink mb-4">Riwayat Sewa</h2>
        {customer.transactions.length === 0 ? (
          <p className="text-sm text-mocha">Belum ada transaksi.</p>
        ) : (
          <div className="space-y-2.5">
            {customer.transactions.map((t) => (
              <Link
                key={t.id}
                to={`/transaksi/${t.id}`}
                data-testid={`history-tx-${t.transaction_number}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-ivory px-4 py-3 hover:bg-muted transition-colors"
              >
                <div>
                  <p className="text-sm font-bold text-brown">{t.transaction_number}</p>
                  <p className="text-xs text-mocha">
                    Pakai {formatDate(t.use_date)} • {t.pcs} pcs • {t.rows.map((r) => r.product_code).join(', ')}
                    {t.rows.some((r) => r.bawahan_name) && (
                      <span className="block mt-0.5">
                        {t.rows.filter((r) => r.bawahan_name).map((r) => `${r.bawahan_code} (${formatSizes(r.bawahan_sizes)})`).join(', ')}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${TX_STATUS[t.status]?.cls}`}>{TX_STATUS[t.status]?.label}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${PAY_STATUS[t.payment_status]?.cls}`}>{PAY_STATUS[t.payment_status]?.label}</span>
                  <span className="text-sm font-semibold">{formatIDR(t.total)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
